import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import { z } from "@/lib/zod-lt";
import { CalendarIcon, UnlockKeyholeIcon, LockKeyholeIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { Combobox } from "@/components/Combobox";
import { ClientForm } from "@/components/ClientForm";
import { ProductForm } from "@/components/ProductForm";
import { InvoiceItemsDataTable } from "@/components/tables/InvoiceItemsDataTable";
import { invoiceItemsColumns } from "@/components/tables/invoiceItemsColumns";
import { UPDATE_INVOICE_ROUTE_ID } from "@/router";
import { useStore } from "@/store";
import { InvoiceItemType, ClientType, ProductType, WaybillType, InvoiceType } from "@/types";
import { WaybillDialog } from "@/components/WaybillDialog";
import { fetchWaybill, fetchInvoice, fetchInvoiceItems, fetchRecentInvoices } from "@/api";
import { downloadBlob } from "@/utils/downloadBlob";

interface InvoiceLoaderData {
  invoice_items: InvoiceItemType[];
  id: number;
  code: string;
  date: string;
  client_id: number;
  payment_method: string | null;
  receipt_code: string | null;
  notes: string | null;
  discarded: boolean;
  waybills?: Array<{ id: number }>;
}

export const CLIENT_ID_FIELD = "client_id";
export const PRODUCT_FIELD = "product";
export const ITEMS_FIELD = "items";
export const DATE_FIELD = "date";
export const PAYMENT_METHOD_FIELD = "payment_method";
export const RECEIPT_CODE_FIELD = "receipt_code";
export const NOTES_FIELD = "notes";

const PAYMENT_METHODS = [
  { label: "Grynais", value: "Grynais" },
  { label: "Pavedimu", value: "Pavedimu" },
];

const intl = new Intl.DateTimeFormat("lt-LT", { dateStyle: "long" });

// `id` is optional because newly-appended items don't have a DB row yet, but
// it MUST be preserved on existing items for the update flow: the store uses
// the presence of `id` to decide between `insert` (new row) and `upsert`
// (update existing row). Without threading `id` through the form state every
// save of an edited invoice would re-insert its items as duplicates.
const invoiceItemSchema = z.object({
  id: z.number().optional(),
  product_id: z.string(),
  name: z.string(),
  latin: z.string().nullable(),
  quantity: z.number().gt(0),
  unit: z.string(),
  price: z.number().gt(0),
});

const formSchema = z.object({
  [CLIENT_ID_FIELD]: z.number({ message: "Pasirinkite klientą." }),
  [ITEMS_FIELD]: z.array(invoiceItemSchema).min(1, { message: "Pridėkite bent vieną prekę." }),
  [DATE_FIELD]: z.date(),
  [PAYMENT_METHOD_FIELD]: z.string(),
  [RECEIPT_CODE_FIELD]: z.string().optional(),
  [NOTES_FIELD]: z.string().optional(),
});

export function InvoiceForm(): React.JSX.Element {
  const loaderData = useRouteLoaderData(UPDATE_INVOICE_ROUTE_ID) as InvoiceLoaderData | undefined;
  const navigate = useNavigate();

  const {
    clients,
    products,
    createInvoice,
    updateInvoice,
    tenant,
    invoices,
    drivers,
    transports,
    waybills,
    setClients,
    setProducts,
    setInvoices,
    isCreateInvoiceLoading,
    isUpdateInvoiceLoading,
    removeInvoiceItem,
  } = useStore((s) => s);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItemType[]>(
    loaderData?.invoice_items ?? [],
  ); // Updated type
  const [invoiceId, setInvoiceId] = useState<number | undefined>(loaderData?.id);
  const [invoiceCode, setInvoiceCode] = useState<string | undefined>(loaderData?.code);
  const [isClientsComboboxOpen, setClientsComboboxOpen] = useState<boolean>(false);
  const [isProductsComboboxOpen, setProductsComboboxOpen] = useState<boolean>(false);
  const [isCreateClientDialogOpen, setCreateClientDialogOpen] = useState<boolean>(false);
  const [isLocked, setLocked] = useState<boolean>(!!loaderData?.id);
  const [isWaybillDialogOpen, setIsWaybillDialogOpen] = useState<boolean>(false);
  const [hasWaybill, setHasWaybill] = useState<boolean>(false);
  const [isDownloadingWaybill, setIsDownloadingWaybill] = useState<boolean>(false);

  // TODO: should be memoized
  const initialDate = loaderData?.date ? new Date(loaderData?.date) : new Date();

  const form = useForm<z.infer<typeof formSchema>>({
    mode: "all",
    resolver: zodResolver(formSchema),
    defaultValues: {
      [CLIENT_ID_FIELD]: loaderData?.client_id,
      [DATE_FIELD]: initialDate,
      [ITEMS_FIELD]: (loaderData?.invoice_items ?? []).map((item) => ({
        // Keep the DB `id` around so updateInvoice() can tell existing rows
        // from newly-appended ones (see invoiceItemSchema comment above).
        id: item.id,
        product_id: item.product_id ?? "",
        name: item.name,
        latin: item.latin,
        quantity: item.quantity,
        unit: item.unit ?? "vnt.",
        price: item.price,
      })),
      [PAYMENT_METHOD_FIELD]: loaderData?.payment_method ?? PAYMENT_METHODS[0].value,
      [RECEIPT_CODE_FIELD]: loaderData?.receipt_code ?? "",
      [NOTES_FIELD]: loaderData?.notes ?? "",
    },
  });

  const invoiceCodes = invoices
    .filter((invoice) => invoice.code && !isNaN(Number(invoice.code)) && !invoice.discarded)
    .map(({ code }) => Number(code));
  const receiptCodes = invoices
    .filter(
      (invoice) =>
        invoice.receipt_code && !isNaN(Number(invoice.receipt_code)) && !invoice.discarded,
    )
    .map(({ receipt_code }) => Number(receipt_code));
  const [lastInvoiceNumber] = invoiceCodes.sort((a, b) => b - a);
  const [lastReceiptNumber] = receiptCodes.sort((a, b) => b - a);
  const lastReceiptCode = String(lastReceiptNumber).padStart(6, "0");
  const newReceiptCode = String(lastReceiptNumber + 1).padStart(6, "0");
  const selectedClient = clients?.find((client) => client.id === form.getValues(CLIENT_ID_FIELD));
  const selectedClientOption = selectedClient
    ? { name: selectedClient.name, id: selectedClient.id }
    : null;
  const payment_method = form.getValues(PAYMENT_METHOD_FIELD);
  const isCash = payment_method === PAYMENT_METHODS[0].value;
  const receiptCode = form.getValues(RECEIPT_CODE_FIELD);
  const receipt_code = isCash && receiptCode ? receiptCode : undefined;
  const client = clients?.find((client) => client.id === form.getValues(CLIENT_ID_FIELD));
  const clientName = client?.name.replace(/\s/g, "-").toLowerCase();
  const fullClientName = `${client?.type ? client.type.toLowerCase() : ""}-${clientName}`;

  useEffect(() => {
    if (form.getValues(PAYMENT_METHOD_FIELD) === PAYMENT_METHODS[1].value) return;
    if (form.getValues(RECEIPT_CODE_FIELD) !== "") return;
    if (isNaN(lastReceiptNumber)) return;
    form.setValue(RECEIPT_CODE_FIELD, newReceiptCode);
  }, [form, newReceiptCode]);

  // Check if waybill exists from invoice data
  useEffect(() => {
    if (loaderData?.id) {
      // Check loaderData which comes from router (single invoice fetch)
      const hasWaybillFromLoader =
        loaderData?.waybills &&
        Array.isArray(loaderData.waybills) &&
        loaderData.waybills.length > 0;

      // Check waybills array directly from Zustand store
      const hasWaybillFromWaybillsArray = waybills.some((w) => w.invoice_id === loaderData.id);

      setHasWaybill(hasWaybillFromLoader || hasWaybillFromWaybillsArray);
    }
  }, [loaderData?.id, loaderData, waybills]);

  form.watch((val) => {
    if (val[ITEMS_FIELD]) {
      setInvoiceItems(val[ITEMS_FIELD] as InvoiceItemType[]);
    }
  });

  const { append, remove } = useFieldArray({
    control: form.control,
    name: ITEMS_FIELD,
  });

  async function handleRemoveRow(index: number) {
    const items = form.getValues(ITEMS_FIELD);
    const item = items[index];
    if (loaderData && item && "id" in item && typeof item.id === "number") {
      await removeInvoiceItem(item.id);
    }
    setInvoiceItems(items.filter((_, i) => i !== index) as InvoiceItemType[]);
    remove(index);
  }

  async function onSubmit(formData: z.infer<typeof formSchema>): Promise<void> {
    const newInvoiceCode = String(lastInvoiceNumber + 1).padStart(6, "0");
    // const { invoice_series } = tenant;
    const currentInvoiceCode = loaderData ? loaderData?.code : newInvoiceCode;
    // const receipt_order_number = `PPK: ${invoice_series} Nr. ${receipt_code} ${formatPPKDate(new Date(form.getValues(DATE_FIELD)))}`;
    // const receipt_order_text = `už sodinukus pagal PVM sąskaitą-faktūrą (specifikaciją) Nr. ${invoice_series} ${currentInvoiceCode}`;

    if (!client) throw new Error("no client found");

    if (loaderData?.id) {
      if (!invoiceId) throw new Error("no invoice id found");

      if (!client) throw new Error("Client is required");
      await updateInvoice({
        client_id: client.id,
        contact_name: client.name,
        id: invoiceId,
        date: formData[DATE_FIELD].toISOString(),
        invoice_items: invoiceItems,
        payment_method: formData[PAYMENT_METHOD_FIELD],
        receipt_code: receipt_code ?? undefined,
        notes: formData[NOTES_FIELD] ?? undefined,
        // receipt_order_number,
        // receipt_order_text,
      });

      // location.reload();
    } else {
      if (!client) throw new Error("Client is required");
      const { id, code } = await createInvoice({
        client_id: client.id,
        contact_name: client.name,
        invoice_items: invoiceItems,
        code: currentInvoiceCode,
        date: formData[DATE_FIELD].toISOString(),
        payment_method: formData[PAYMENT_METHOD_FIELD],
        receipt_code: receipt_code ?? undefined,
        notes: formData[NOTES_FIELD] ?? undefined,
        // receipt_order_number,
        // receipt_order_text,
      });

      setInvoiceId(id);
      setInvoiceCode(code);
      navigate(`/invoices/${id}`);
    }
  }

  function appendInvoiceItem(product: {
    id: string;
    name: string;
    latin: string | null;
    price: number | null;
  }) {
    append({
      product_id: product.id,
      name: product.name,
      latin: product.latin,
      quantity: 1,
      unit: "vnt.", // TODO: should come from settings and from product
      price: product.price ?? 0,
    });
  }

  function handleProductSelect(option: { id: string | number }): void {
    const product = products?.find((product) => product.id === String(option.id));
    if (!product) throw new Error("no product found");
    appendInvoiceItem(product);
  }

  function handleClientCreate(clientFormData: {
    name: string;
    id?: number;
    code?: string;
    contact_name?: string;
    phone?: string;
    address?: string;
    type?: string;
    vat?: string;
    contract_id?: string;
  }) {
    if (!clientFormData.id) return;
    const newClient =
      clients.find((c) => c.id === clientFormData.id) ??
      ({ id: clientFormData.id, name: clientFormData.name } as ClientType);
    setClients([newClient, ...clients]);
    setCreateClientDialogOpen(false);
    setClientsComboboxOpen(false);
    form.setValue(CLIENT_ID_FIELD, clientFormData.id);
    form.trigger(CLIENT_ID_FIELD);
  }

  function handleProductCreate(productFormData: {
    name: string;
    price: string;
    id?: string;
    latin?: string;
    unit?: string;
  }) {
    if (!productFormData.id) return;
    const newProduct =
      products.find((p) => p.id === productFormData.id) ??
      ({
        id: productFormData.id,
        name: productFormData.name,
        latin: productFormData.latin ?? null,
        price: productFormData.price ? Number(productFormData.price) : null,
      } as ProductType);
    setProducts([...products, newProduct]);
    setProductsComboboxOpen(false);
    appendInvoiceItem(newProduct);
  }

  async function createPdfDocument() {
    const { getPdfDocument } = await import("@/utils/getPdfDocument");

    if (!tenant) throw new Error("Tenant is required");
    if (!client) throw new Error("Client is required");
    if (!invoiceCode) throw new Error("Invoice code is required");

    return await getPdfDocument({
      tenant,
      client,
      invoiceCode,
      notes: form.getValues(NOTES_FIELD) ?? undefined,
      receiptCode: receipt_code,
      date: new Date(form.getValues(DATE_FIELD)),
      invoiceItems,
    });
  }

  // Instead of downloading from the bucket, let's create it on the fly
  async function handleDownloadButtonClick() {
    const pdf = await createPdfDocument();

    // Create a safe filename by sanitizing the date (use ISO format instead of formatted date)
    const safeDate = new Date(form.getValues(DATE_FIELD)).toISOString().split("T")[0]; // YYYY-MM-DD format
    const code = invoiceCode || loaderData?.code || "draft";

    // Remove any remaining invalid characters and create filename
    const filename =
      `${code}-${safeDate}-${fullClientName}`.replace(/[^a-zA-Z0-9-]/g, "-") + ".pdf";

    // Use an explicit anchor download instead of pdfmake's `.download()` so the
    // behavior is consistent across browsers (Safari/iOS would otherwise open
    // the PDF inline in the browser viewer).
    const blob = await pdf.getBlob();
    downloadBlob(blob, filename);
  }

  function handlePublishButtonClick() {
    // setDraft(false)
  }

  async function handleDownloadWaybillClick() {
    if (!loaderData?.id || !tenant || !client) return;

    setIsDownloadingWaybill(true);
    try {
      // Fetch all required data
      const [waybillResponse, invoiceResponse, invoiceItemsResponse] = await Promise.all([
        fetchWaybill(String(loaderData.id)),
        fetchInvoice(String(loaderData.id)),
        fetchInvoiceItems(String(loaderData.id)),
      ]);

      if (waybillResponse.error || invoiceResponse.error || invoiceItemsResponse.error) {
        throw new Error("Nepavyko gauti visų reikalingų duomenų");
      }

      const waybill = waybillResponse.data;
      const invoice = invoiceResponse.data;
      const invoiceItems = invoiceItemsResponse.data;

      if (!waybill || !invoice || !invoiceItems) {
        throw new Error("Nepavyko gauti visų reikalingų duomenų");
      }

      // Use drivers and transports from store (loaded on app init)
      if (!drivers || drivers.length === 0 || !transports || transports.length === 0) {
        throw new Error("Vairuotojų ar transporto duomenys nėra užkrauti");
      }

      // Type guards for Supabase responses
      const waybillData = waybill as unknown;
      const invoiceData = invoice as unknown;
      const invoiceItemsData = invoiceItems as unknown;

      if (
        typeof waybillData !== "object" ||
        waybillData === null ||
        "error" in waybillData ||
        typeof invoiceData !== "object" ||
        invoiceData === null ||
        "error" in invoiceData ||
        !Array.isArray(invoiceItemsData)
      ) {
        throw new Error("Neteisingi duomenų tipai");
      }

      const validWaybill = waybillData as WaybillType;
      const validInvoice = invoiceData as InvoiceType;
      const validInvoiceItems = invoiceItemsData as InvoiceItemType[];
      const validDrivers = drivers;
      const validTransports = transports;

      const driver = validDrivers.find((d) => d.id === validWaybill.driver_id);
      const transport = validTransports.find((t) => t.id === validWaybill.transport_id);

      if (!driver || !transport) {
        throw new Error("Nepavyko rasti vairuotojo ar transporto duomenų");
      }

      // Generate and download PDF
      const { getWaybillPdf } = await import("@/utils/getWaybillPdf");
      const pdf = await getWaybillPdf({
        tenant,
        waybill: validWaybill,
        driver,
        transport,
        invoice: validInvoice,
        client,
        invoiceItems: validInvoiceItems,
      });

      const safeDate = new Date(validWaybill.loading_date || new Date())
        .toISOString()
        .split("T")[0];
      const filename =
        `vaztarastis-${validInvoice.code || loaderData.code}-${safeDate}`.replace(
          /[^a-zA-Z0-9-]/g,
          "-",
        ) + ".pdf";

      const blob = await pdf.getBlob();
      downloadBlob(blob, filename);
    } catch (error) {
      console.error("Error generating waybill PDF:", error);
      alert(
        `Klaida generuojant važtaraščio PDF: ${error instanceof Error ? error.message : "Nežinoma klaida"}`,
      );
    } finally {
      setIsDownloadingWaybill(false);
    }
  }

  if (!clients || !products) return <></>;

  const clientOptions = clients
    .map(({ name, id }) => ({ name, id }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const productOptions = products
    .map((product) => {
      const properties = [product.age, product.type, product.id, `${product.price} €`]
        .filter(Boolean)
        .join(" | ");

      return {
        name: `${product.name} (${properties})`,
        id: product.id,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  function handleClientSelect(option: { id: string | number }): void {
    const id = typeof option.id === "number" ? option.id : Number(option.id);
    if (!isNaN(id)) {
      form.setValue(CLIENT_ID_FIELD, id);
      form.trigger(CLIENT_ID_FIELD);
    }
  }

  const buttonIdleLabel = loaderData?.id ? "Atnaujinti" : "Sukurti";
  const buttonLoadingLabel = loaderData?.id ? "Atnaujinama..." : "Kuriama...";
  const isIvoiceLoading = isCreateInvoiceLoading || isUpdateInvoiceLoading;
  const buttonLabel = isIvoiceLoading ? buttonLoadingLabel : buttonIdleLabel;

  const format = (date: Date) => intl.format(new Date(date));

  return (
    <div className="my-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-8 grid-cols-2">
            <FormField
              control={form.control}
              name={CLIENT_ID_FIELD}
              render={() => (
                <FormItem className="flex flex-col">
                  <FormLabel>Klientas</FormLabel>
                  <Combobox
                    options={clientOptions}
                    isOpen={isClientsComboboxOpen}
                    onComboboxOpenChange={setClientsComboboxOpen}
                    selectedOption={selectedClientOption}
                    onSelect={handleClientSelect}
                    selectLabel="Pasirinkite klientą"
                    searchLabel="Ieškoti kliento..."
                    notFoundLabel="Klientas nerastas."
                    dialogTitle="Naujas klientas"
                    dialogDescription="Sukurkite naują klientą."
                    isDialogOpen={isCreateClientDialogOpen}
                    onDialogOpenChange={setCreateClientDialogOpen}
                    disabled={isLocked}
                    data-testid="client-combobox"
                  >
                    <ClientForm onClientFormSubmit={handleClientCreate} />
                  </Combobox>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={DATE_FIELD}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Data</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground",
                          )}
                          disabled={isLocked}
                          data-testid="date-picker-trigger"
                        >
                          {field.value ? format(field.value) : <span>Pasirinkite datą</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      trackingValue={field.value}
                      data-testid="date-picker-content"
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <InvoiceItemsDataTable<InvoiceItemType, unknown, z.infer<typeof formSchema>>
            columns={invoiceItemsColumns}
            form={form}
            data={invoiceItems}
            disabled={isLocked}
            onRemoveRow={handleRemoveRow}
            data-testid="invoice-items-table"
          >
            <Combobox
              options={productOptions}
              isOpen={isProductsComboboxOpen}
              onComboboxOpenChange={setProductsComboboxOpen}
              selectedOption={null}
              onSelect={handleProductSelect}
              selectLabel="Pridėti prekę"
              searchLabel="Ieškoti prekės..."
              notFoundLabel="Prekė nerasta."
              disabled={isLocked}
              data-testid="product-combobox"
            >
              <ProductForm onProductFormSubmit={handleProductCreate} />
            </Combobox>
          </InvoiceItemsDataTable>
          {form.formState.errors[ITEMS_FIELD] && (
            <div className="text-[0.8rem] font-medium text-destructive">
              {form.formState.errors[ITEMS_FIELD].message}
            </div>
          )}
          <div className="grid gap-8 grid-cols-2">
            <div>
              <FormField
                control={form.control}
                name={PAYMENT_METHOD_FIELD}
                render={({ field }) => (
                  <FormItem className="flex flex-col mb-4">
                    <FormLabel>Apmokėjimo būdas</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={isLocked}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="payment-method-select-trigger">
                          <SelectValue placeholder="Pasirinkite apmokėjimo būdą" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PAYMENT_METHODS.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {isCash && (
                <FormField
                  control={form.control}
                  name={RECEIPT_CODE_FIELD}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="block">Kasos Orderio Numeris*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLocked} data-testid="receipt-code-input" />
                      </FormControl>
                      <FormDescription>
                        Paskutinis išrašytas kasos orderio numeris sistemoje:{" "}
                        <b>{lastReceiptCode}</b>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <FormField
              control={form.control}
              name={NOTES_FIELD}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Pastabos</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      disabled={isLocked}
                      rows={6}
                      data-testid="notes-textarea"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex">
            <Button
              className="mr-4"
              type="submit"
              disabled={isIvoiceLoading || isLocked}
              onClick={handlePublishButtonClick}
              data-testid="save-invoice-button"
            >
              {buttonLabel}
            </Button>
            {loaderData?.id && !loaderData.discarded && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="mr-4"
                      type="button"
                      variant={isLocked ? "secondary" : "outline"}
                      onClick={() => setLocked(!isLocked)}
                    >
                      {isLocked ? (
                        <LockKeyholeIcon className="h-4 w-4" />
                      ) : (
                        <UnlockKeyholeIcon className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Spustelkite šią ikoną, kad galėtumėte redaguoti jau sukurtą sąskaitą</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </form>
      </Form>
      {!!loaderData && (
        <div className="mt-4 flex gap-4">
          <Button onClick={handleDownloadButtonClick} disabled={!tenant || !client || !invoiceCode}>
            Parsisiųsti
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsWaybillDialogOpen(true)}
            disabled={!tenant || !client || !invoiceCode}
          >
            {hasWaybill ? "Redaguoti važtaraštį" : "Sukurti naują važtaraštį"}
          </Button>
          {hasWaybill && (
            <Button
              variant="outline"
              onClick={handleDownloadWaybillClick}
              disabled={!tenant || !client || !invoiceCode || isDownloadingWaybill}
            >
              {isDownloadingWaybill ? "Generuojama..." : "Parsisiųsti važtaraštį"}
            </Button>
          )}
        </div>
      )}
      {!!loaderData?.id && (
        <WaybillDialog
          open={isWaybillDialogOpen}
          onOpenChange={(open) => {
            setIsWaybillDialogOpen(open);
            // Refresh waybill status when dialog closes
            // The waybill state will be updated automatically via the useEffect that watches waybills array
            // But we can also check immediately using the current waybills from the closure
            if (!open && loaderData?.id) {
              // The useEffect will automatically update hasWaybill when waybills changes
              // But we can also re-fetch invoices to get updated waybill status from database
              fetchRecentInvoices().then(({ data, error }) => {
                if (!error && data) {
                  // @ts-expect-error - Supabase types include error unions, but data is valid when !error
                  setInvoices(data);
                  // The useEffect will pick up the changes automatically
                }
              });
            }
          }}
          invoiceId={loaderData.id}
        />
      )}
    </div>
  );
}
